import { LANGUAGE_FR } from 'shared/consts/languages'
import {
  TENANT_AT,
  TENANT_CH,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts/tenants'
import {
  EXPERIMENT_VARIATION,
  type ExperimentName,
  SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMobile } from 'shared/utils/deviceClassUtils'
import type { Experiment, Tenant } from 'types/shopConfig'

export const isObjKey = <T extends object>(
  key: PropertyKey,
  obj: T
): key is keyof T => key in obj

export const getExperimentNamePerTenant = (
  tenant: Tenant,
  deviceClass: string
): ExperimentName => {
  return (
    (isObjKey(tenant, SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT) &&
      SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT[tenant][
        isMobile(deviceClass) ? 'mobile' : 'desktop'
      ]) ||
    ''
  )
}

export const preQuerySuggestionsExperimentEnabledAndNotDefault = (
  tenant: Tenant,
  deviceClass: string,
  experiments: Experiment[]
) => {
  const { isEnabled, variant } = getExperiment(
    getExperimentNamePerTenant(tenant, deviceClass),
    experiments
  )
  return isEnabled && variant !== EXPERIMENT_VARIATION.DEFAULT
}

export const getPopularCategoriesPerTenant = (tenant: Tenant) => {
  switch (tenant) {
    case TENANT_COM:
      return [
        {
          name: 'Vitamine & Mineralstoffe',
          url: '/vitamine-mineralstoffe-enzyme/',
        },
        {
          name: 'Magen & Darm',
          url: '/magen-darm/',
        },
        {
          name: 'Schmerzen',
          url: '/schmerzmittel/',
        },
      ]
    case TENANT_AT:
      return [
        {
          name: 'Vitamine & Mineralstoffe',
          url: '/vitamine-mineralstoffe-enzyme/',
        },
        {
          name: 'Erkältung & Grippe',
          url: '/erkaeltung/',
        },
        {
          name: 'Magen & Darm',
          url: '/magen-darm/',
        },
      ]
    case TENANT_IT:
      return [
        {
          name: 'Stomaco e Intestino',
          url: '/stomaco-intestino/',
        },
        {
          name: 'Viso',
          url: '/viso/',
        },
        {
          name: 'Alimentazione',
          url: '/alimentazione-dieta/',
        },
      ]
    case TENANT_FR:
      return [
        {
          name: 'Compléments santé',
          url: '/complements-sante/',
        },
        {
          name: 'Alimentation animale',
          url: '/nourriture-animaux/',
        },
        {
          name: 'Visage',
          url: '/visage/',
        },
      ]
    default:
      return null
  }
}

export const getPopularBrandsPerTenant = (tenant: Tenant) => {
  switch (tenant) {
    case TENANT_COM:
      return [
        {
          name: 'CeraVe',
          url: '/markenshops/ms-cerave/',
        },
        {
          name: 'La Roche Posay',
          url: '/markenshops/roche-posay-produktlinien/',
        },
        {
          name: 'Bepanthen',
          url: '/marken/bepanthen/',
        },
      ]
    case TENANT_AT:
      return [
        {
          name: 'CeraVe',
          url: '/cerave-beauty/',
        },
        {
          name: 'Bepanthen',
          url: '/marken/bepanthen/',
        },
        {
          name: 'La Roche Posay',
          url: '/la-roche-posay-beauty/all-from-category/',
        },
      ]
    case TENANT_IT:
      return [
        {
          name: 'CeraVe',
          url: '/marchi/cerave/',
        },
        {
          name: 'La Roche-Posay',
          url: '/marchi/la-roche-posay/',
        },
        {
          name: 'Avéne',
          url: '/marchi/avene/',
        },
      ]
    case TENANT_FR:
      return [
        {
          name: 'Cerave',
          url: '/marques/cerave-marque/',
        },
        {
          name: 'Avéne',
          url: '/marques/avene/',
        },
        {
          name: 'La Roche-Posay',
          url: '/espace-marques/la-roche-posay/',
        },
      ]
    default:
      return null
  }
}

export const getTranslationsPerTenant = (tenant: Tenant, language: string) => {
  switch (tenant) {
    case TENANT_FR:
      return {
        categories: 'Catégories populaires',
        brands: 'Marques populaires',
      }
    case TENANT_IT:
      return {
        categories: 'Categorie popolari',
        brands: 'Marchi popolari',
      }
    case TENANT_CH:
      return language === LANGUAGE_FR
        ? {
            categories: 'Catégories populaires',
            brands: 'Marques populaires',
          }
        : {
            categories: 'Beliebte Kategorien',
            brands: 'Beliebte Marken',
          }
    default:
      return {
        categories: 'Beliebte Kategorien',
        brands: 'Beliebte Marken',
      }
  }
}
